"use client";

import { Component, ReactNode } from "react";
import { createLogger } from "@/lib/logger";
import { AlertTriangle, RefreshCw } from "lucide-react";

const logger = createLogger("ChartErrorBoundary");

interface ChartErrorBoundaryProps {
  children: ReactNode;
  chartName?: string;
  height?: string;
  /**
   * Already-translated fallback copy. Class components can't call
   * useT() directly, so callers wrap the boundary with a functional
   * shell that resolves these via t() and forwards them. Both fall
   * back to English if a caller hasn't been migrated yet.
   */
  errorLabel?: string;
  retryLabel?: string;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary specifically for chart components.
 * Catches rendering errors in charts and displays a user-friendly fallback.
 */
export class ChartErrorBoundary extends Component<ChartErrorBoundaryProps, ChartErrorBoundaryState> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("Chart rendering error", {
      chartName: this.props.chartName || "Unknown",
      error: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={`flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-[5px] border border-gray-200 dark:border-gray-800 p-6 ${this.props.height || "h-[200px]"}`}
        >
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-3">
            {this.props.errorLabel
              ?? (this.props.chartName ? `Failed to load ${this.props.chartName}` : "Chart failed to load")}
          </p>
          <button
            onClick={this.handleRetry}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-[5px] hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            {this.props.retryLabel ?? "Retry"}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
